import { jsx as _jsx } from "react/jsx-runtime";
import { collection, doc, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { db } from "../firebase/config";
import { useAuth } from "../auth/AuthProvider";
import { getAdminSupportTeamId } from "../admin/adminSupport";
const TeamContext = createContext(undefined);
const teamStorageKey = (userId) => `mtft:selectedTeam:${userId}`;
const seasonStorageKey = (userId, teamId) => `mtft:selectedSeason:${userId}:${teamId}`;
function meetFromDocument(meetDocument) {
    const meet = meetDocument.data();
    const location = typeof meet.location === "string"
        ? meet.location
        : meet.location?.name ?? meet.address ?? "Location TBD";
    const entries = typeof meet.entries === "number"
        ? meet.entries
        : Array.isArray(meet.entries) ? meet.entries.length : 0;
    return {
        id: meetDocument.id,
        name: meet.name ?? "Untitled meet",
        date: new Date(meet.dateTime),
        location,
        type: meet.type ?? "",
        entries,
        course: meet.course,
        opponent: meet.opponent,
        score: meet.score,
    };
}
function seasonOrder(season) {
    const normalized = season.name.trim().toLowerCase();
    if (season.type === "cross-country" || normalized === "cross country")
        return 0;
    if (season.type === "outdoor-track" || normalized === "outdoor")
        return 1;
    if (season.type === "indoor-track" || normalized === "indoor")
        return 2;
    return 3;
}
function seasonEndYear(season) {
    const normalized = season.name.trim().toLowerCase();
    return season.type === "indoor-track" || normalized === "indoor" ? season.year + 1 : season.year;
}
export function formatTeamName(team) {
    const adultDivision = team.level === "college" || team.level === "club";
    const division = team.gender === "female"
        ? adultDivision ? "Women" : "Girls"
        : adultDivision ? "Men" : "Boys";
    return `${team.name} ${division}`;
}
export function getSeasons(seasons) {
    if (!seasons)
        return [];
    if (Array.isArray(seasons))
        return seasons;
    return Object.entries(seasons).map(([seasonId, season]) => ({
        ...season,
        id: season.id || seasonId,
    }));
}
export function sortSeasons(seasons) {
    return getSeasons(seasons).sort((left, right) => {
        const endYearDifference = seasonEndYear(right) - seasonEndYear(left);
        return endYearDifference
            || seasonOrder(left) - seasonOrder(right)
            || right.year - left.year
            || left.name.localeCompare(right.name);
    });
}
export function formatSeasonName(season) {
    const normalized = season.name.trim().toLowerCase();
    let name;
    if (normalized === "outdoor")
        name = `Outdoor Track ${season.year}`;
    else if (normalized === "indoor")
        name = `Indoor Track ${season.year} - ${season.year + 1}`;
    else if (normalized === "cross country")
        name = `Cross Country ${season.year}`;
    else
        name = `${season.name} ${season.year}`;
    return season.description?.trim() ? `${season.description.trim()} ${name}` : name;
}
export function TeamProvider({ children }) {
    const { user, isAdmin } = useAuth();
    const adminSupportTeamId = isAdmin ? getAdminSupportTeamId() : "";
    const [teams, setTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState("");
    const [selectedSeasonId, setSelectedSeasonId] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [athletes, setAthletes] = useState([]);
    const [athleteCountLoading, setAthleteCountLoading] = useState(false);
    const [upcomingMeets, setUpcomingMeets] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [upcomingMeetsLoading, setUpcomingMeetsLoading] = useState(false);
    useEffect(() => {
        if (!user || (isAdmin && !adminSupportTeamId)) {
            setTeams([]);
            setSelectedTeamId("");
            setSelectedSeasonId("");
            setLoading(false);
            setError("");
            return;
        }
        setLoading(true);
        setError("");
        if (adminSupportTeamId) {
            return onSnapshot(doc(db, "teams", adminSupportTeamId), (teamDocument) => {
                if (!teamDocument.exists()) {
                    setTeams([]);
                    setSelectedTeamId("");
                    setError("This team no longer exists or could not be loaded.");
                    setLoading(false);
                    return;
                }
                const team = { ...teamDocument.data(), id: teamDocument.id };
                setTeams([team]);
                setSelectedTeamId(team.id);
                setLoading(false);
            }, (caughtError) => {
                setTeams([]);
                setSelectedTeamId("");
                setError(caughtError.message || "The team could not be loaded.");
                setLoading(false);
            });
        }
        const teamsQuery = query(collection(db, "teams"), where("managers", "array-contains", user.uid));
        return onSnapshot(teamsQuery, (snapshot) => {
            const loadedTeams = snapshot.docs
                .map((teamDocument) => ({ ...teamDocument.data(), id: teamDocument.id }))
                .sort((left, right) => left.name.localeCompare(right.name));
            const savedTeamId = localStorage.getItem(teamStorageKey(user.uid));
            setTeams(loadedTeams);
            setSelectedTeamId((currentTeamId) => {
                if (currentTeamId && loadedTeams.some((team) => team.id === currentTeamId))
                    return currentTeamId;
                const initialTeam = loadedTeams.find((team) => team.id === savedTeamId) ?? loadedTeams[0] ?? null;
                return initialTeam?.id ?? "";
            });
            setLoading(false);
        }, (caughtError) => {
            setError(caughtError.message || "Teams could not be loaded.");
            setLoading(false);
        });
    }, [adminSupportTeamId, isAdmin, user]);
    const selectedTeam = teams.find((team) => team.id === selectedTeamId) ?? null;
    const sortedSeasons = useMemo(() => sortSeasons(selectedTeam?.seasons), [selectedTeam]);
    useEffect(() => {
        if (!user || !selectedTeam) {
            setSelectedSeasonId("");
            return;
        }
        localStorage.setItem(teamStorageKey(user.uid), selectedTeam.id);
        const savedSeasonId = localStorage.getItem(seasonStorageKey(user.uid, selectedTeam.id));
        const initialSeason = sortedSeasons.find((season) => season.id === savedSeasonId) ?? sortedSeasons[0] ?? null;
        setSelectedSeasonId(initialSeason?.id ?? "");
    }, [selectedTeam, sortedSeasons, user]);
    const selectedSeason = sortedSeasons.find((season) => season.id === selectedSeasonId) ?? null;
    const selectedRole = selectedTeam && user
        ? adminSupportTeamId === selectedTeam.id ? "owner" : selectedTeam.owner.id === user.uid ? "owner" : "manager"
        : null;
    useEffect(() => {
        if (!selectedTeam || !selectedSeason) {
            setAthletes([]);
            setAthleteCountLoading(false);
            return;
        }
        setAthleteCountLoading(true);
        const rosterQuery = query(collection(db, "teams", selectedTeam.id, "roster"), where("seasons", "array-contains", selectedSeason.id));
        return onSnapshot(rosterQuery, (snapshot) => {
            const loadedAthletes = snapshot.docs
                .map((athleteDocument) => {
                const athlete = athleteDocument.data();
                return {
                    id: athlete.id ?? athleteDocument.id,
                    firstName: athlete.firstName ?? "",
                    lastName: athlete.lastName ?? "",
                    classOf: Number(athlete.classOf),
                    seasons: Array.isArray(athlete.seasons) ? athlete.seasons : [],
                    type: athlete.type ?? "Other",
                    photo: athlete.photo,
                };
            })
                .sort((left, right) => left.lastName.localeCompare(right.lastName) || left.firstName.localeCompare(right.firstName));
            setAthletes(loadedAthletes);
            setAthleteCountLoading(false);
        }, () => {
            setAthletes([]);
            setAthleteCountLoading(false);
        });
    }, [selectedSeason, selectedTeam]);
    useEffect(() => {
        if (!selectedTeam || !selectedSeason) {
            setUpcomingMeets([]);
            setSchedule([]);
            setUpcomingMeetsLoading(false);
            return;
        }
        setUpcomingMeetsLoading(true);
        const scheduleQuery = query(collection(db, "teams", selectedTeam.id, "schedule"), where("seasonId", "==", selectedSeason.id));
        const upcomingQuery = query(collection(db, "teams", selectedTeam.id, "schedule"), where("seasonId", "==", selectedSeason.id), where("dateTime", ">", Date.now()), orderBy("dateTime", "asc"), limit(3));
        let scheduleLoaded = false;
        let upcomingLoaded = false;
        const finishLoading = () => {
            if (scheduleLoaded && upcomingLoaded)
                setUpcomingMeetsLoading(false);
        };
        const unsubscribeSchedule = onSnapshot(scheduleQuery, (snapshot) => {
            setSchedule(snapshot.docs.map(meetFromDocument).sort((left, right) => left.date.getTime() - right.date.getTime()));
            scheduleLoaded = true;
            finishLoading();
        }, () => {
            setSchedule([]);
            scheduleLoaded = true;
            finishLoading();
        });
        const unsubscribeUpcoming = onSnapshot(upcomingQuery, (snapshot) => {
            setUpcomingMeets(snapshot.docs.map(meetFromDocument));
            upcomingLoaded = true;
            finishLoading();
        }, () => {
            setUpcomingMeets([]);
            upcomingLoaded = true;
            finishLoading();
        });
        return () => {
            unsubscribeSchedule();
            unsubscribeUpcoming();
        };
    }, [selectedSeason, selectedTeam]);
    function selectTeam(teamId) {
        setSelectedTeamId(teamId);
    }
    function selectSeason(seasonId) {
        if (!user || !selectedTeam)
            return;
        setSelectedSeasonId(seasonId);
        localStorage.setItem(seasonStorageKey(user.uid, selectedTeam.id), seasonId);
    }
    function updateSelectedTeam(updates) {
        if (!selectedTeam)
            return;
        setTeams((current) => current.map((team) => team.id === selectedTeam.id ? { ...team, ...updates } : team));
    }
    function updateSelectedSeason(updates) {
        if (!selectedTeam || !selectedSeason)
            return;
        setTeams((current) => current.map((team) => team.id === selectedTeam.id
            ? { ...team, seasons: { ...team.seasons, [selectedSeason.id]: { ...selectedSeason, ...updates } } }
            : team));
    }
    function addSeasonLocally(teamId, season) {
        setTeams((current) => current.map((candidate) => candidate.id === teamId
            ? { ...candidate, seasons: { ...(candidate.seasons ?? {}), [season.id]: season } }
            : candidate));
        if (user && selectedTeamId === teamId) {
            setSelectedSeasonId(season.id);
            localStorage.setItem(seasonStorageKey(user.uid, teamId), season.id);
        }
    }
    function removeSeasonLocally(teamId, seasonId) {
        const team = teams.find((candidate) => candidate.id === teamId);
        if (!team)
            return "";
        const remainingSeasons = { ...(team.seasons ?? {}) };
        delete remainingSeasons[seasonId];
        const nextSeasonId = sortSeasons(remainingSeasons)[0]?.id ?? "";
        setTeams((current) => current.map((candidate) => candidate.id === teamId
            ? { ...candidate, seasons: remainingSeasons }
            : candidate));
        if (user && selectedTeamId === teamId && selectedSeasonId === seasonId) {
            setSelectedSeasonId(nextSeasonId);
            const storageKey = seasonStorageKey(user.uid, teamId);
            if (nextSeasonId)
                localStorage.setItem(storageKey, nextSeasonId);
            else
                localStorage.removeItem(storageKey);
        }
        return nextSeasonId;
    }
    function removeTeamLocally(teamId) {
        const remaining = teams.filter((team) => team.id !== teamId);
        setTeams(remaining);
        if (selectedTeamId === teamId)
            setSelectedTeamId(remaining[0]?.id ?? "");
    }
    return (_jsx(TeamContext.Provider, { value: { teams, selectedTeam, selectedSeason, selectedRole, athleteCount: athletes.length, athleteCountLoading, athletes, upcomingMeets, upcomingMeetsLoading, schedule, loading, error, selectTeam, selectSeason, updateSelectedTeam, updateSelectedSeason, addSeasonLocally, removeSeasonLocally, removeTeamLocally }, children: children }));
}
export function useTeams() {
    const context = useContext(TeamContext);
    if (!context)
        throw new Error("useTeams must be used within TeamProvider");
    return context;
}
